import { useState } from "react";
import {
  Home,
  BookOpen,
  Users,
  Compass,
  LayoutDashboard,
  User,
  Gift,
  LogOut,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "@/lib/router-compat";
import { useAuth } from "@/hooks/useAuth";
import { useSignOut } from "@/hooks/useSignOut";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

const tabs = [
  { label: "Home", icon: Home, href: "/" },
  { label: "Learn", icon: BookOpen, href: "/learn" },
  { label: "Experts", icon: Users, href: "/experts" },
  { label: "Tools", icon: Compass, href: "/tools" },
];

const MobileBottomNav = () => {
  const location = useLocation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const signOut = useSignOut("/");
  const [accountOpen, setAccountOpen] = useState(false);

  const hiddenRoutes = ["/lifemart/survey", "/readiness-check", "/embed"];
  if (hiddenRoutes.some((r) => location.pathname.startsWith(r))) return null;

  const itemClass = (active: boolean) =>
    `flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg transition-colors min-w-0 flex-1 ${
      active ? "text-primary" : "text-muted-foreground hover:text-foreground"
    }`;

  const go = (href: string) => {
    setAccountOpen(false);
    navigate(href);
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-card/95 backdrop-blur-md xl:hidden safe-area-bottom">
      <div className="flex items-center justify-around px-1 py-1.5">
        {tabs.map((tab) => {
          const isActive = location.pathname === tab.href;
          return (
            <Link key={tab.label} to={tab.href} className={itemClass(isActive)}>
              <tab.icon size={20} strokeWidth={isActive ? 2.5 : 2} />
              <span className="text-[10px] font-medium leading-tight truncate">
                {tab.label}
              </span>
            </Link>
          );
        })}

        {user && (
          <Sheet open={accountOpen} onOpenChange={setAccountOpen}>
            <SheetTrigger
              aria-label="Account menu"
              className={itemClass(location.pathname === "/dashboard")}
            >
              <LayoutDashboard
                size={20}
                strokeWidth={location.pathname === "/dashboard" ? 2.5 : 2}
              />
              <span className="text-[10px] font-medium leading-tight truncate">
                Account
              </span>
            </SheetTrigger>
            <SheetContent side="bottom" className="rounded-t-2xl pb-8">
              <SheetHeader className="text-left">
                <SheetTitle className="text-base">
                  {user.email ?? "Your account"}
                </SheetTitle>
              </SheetHeader>
              <div className="mt-4 flex flex-col">
                <button
                  type="button"
                  onClick={() => go("/dashboard")}
                  className="flex items-center gap-3 rounded-lg px-2 py-3 text-sm text-foreground hover:bg-muted"
                >
                  <LayoutDashboard size={18} /> Dashboard
                </button>
                <button
                  type="button"
                  onClick={() => go("/profile/complete")}
                  className="flex items-center gap-3 rounded-lg px-2 py-3 text-sm text-foreground hover:bg-muted"
                >
                  <User size={18} /> Profile
                </button>
                <button
                  type="button"
                  onClick={() => go("/rewards")}
                  className="flex items-center gap-3 rounded-lg px-2 py-3 text-sm text-foreground hover:bg-muted"
                >
                  <Gift size={18} /> Rewards
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAccountOpen(false);
                    void signOut();
                  }}
                  className="mt-1 flex items-center gap-3 rounded-lg px-2 py-3 text-sm font-semibold text-destructive hover:bg-destructive/10"
                >
                  <LogOut size={18} /> Sign Out
                </button>
              </div>
            </SheetContent>
          </Sheet>
        )}
      </div>
    </nav>
  );
};

export default MobileBottomNav;
